import { memo, useMemo } from 'react';
import { Avatar, Box, Button, IconButton as MuiIconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { InventoryDownloadService } from 'modules/export/services';
import { INotification, NOTIFICATION_TYPE } from '../../interfaces/INotification';
import { FlexBox } from '@dfl/mui-react-common';
import { CloseOutlined } from '@mui/icons-material';

interface NotificationToastItemProps {
  notification: INotification;
  onDismiss?: () => void;
  toastId?: string;
  visible?: boolean;
}

// Global download lock to prevent multiple automatic downloads
const downloadLocks = new Map<string, number>();

const checkDownloadLock = (downloadId: string, cooldownMs: number = 2000): boolean => {
  const now = Date.now();
  const lastDownload = downloadLocks.get(downloadId);

  if (lastDownload && now - lastDownload < cooldownMs) {
    return false; // Block download
  }

  downloadLocks.set(downloadId, now);
  return true; // Allow download
};

const NotificationToastItem = ({ notification, onDismiss, toastId, visible = true }: NotificationToastItemProps) => {
  const { t } = useTranslation('notification');

  const getInitials = (name?: string, firstName?: string, lastName?: string) => {
    if (name) {
      // Get first letter of first two words
      const words = name.split(' ').filter((word) => word.length > 0);
      if (words.length >= 2) {
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const notificationTitle = useMemo(() => {
    return t(`titles.${notification?.type}`, { defaultValue: t('toast.newNotification') });
  }, [notification?.type, t]);

  const notificationDescription = useMemo(() => {
    if (
      notification?.type === NOTIFICATION_TYPE.SUPPLIER_CREATE ||
      notification?.type === NOTIFICATION_TYPE.MANUFACTURE_CREATE ||
      notification?.type === NOTIFICATION_TYPE.LOGISTIC_PROVIDER_CREATE ||
      notification?.type === NOTIFICATION_TYPE.DISTRIBUTION_CENTER_CREATE ||
      notification?.type === NOTIFICATION_TYPE.WAREHOUSE_CREATE ||
      notification?.type === NOTIFICATION_TYPE.CATEGORY_CREATE
    ) {
      return (
        <Typography variant='body2' color='text.secondary'>
          <strong>{notification.data?.name || t('common:unknown')}</strong>
          <span> {t('descriptions.SUPPLIER_CREATE')} </span>
          <strong>{notification.executedBy?.fullName || t('common:someone')}</strong>
        </Typography>
      );
    }

    if (notification?.type === NOTIFICATION_TYPE.EXPORT_DONE) {
      return (
        <Typography variant='body2' color='text.secondary'>
          <strong>{notification.data?.file?.filename || notification.data?.name || t('common:unknown')}</strong>
          <span> {t('descriptions.EXPORT_DONE', { defaultValue: 'is ready for download' })} </span>
        </Typography>
      );
    }

    if (notification?.type === NOTIFICATION_TYPE.EXPORT_FAILED) {
      return (
        <Typography variant='body2' color='text.secondary'>
          <strong>{notification.data?.name || t('common:unknown')}</strong>
          <span> {notification.message || t('messages.EXPORT_FAILED_MESSAGE', { defaultValue: 'has failed and has been removed from the queue.' })} </span>
        </Typography>
      );
    }

    if (notification?.type === NOTIFICATION_TYPE.RECOVERY_PAID_CANCELLED_ORDER) {
      return (
        <Typography variant='body2' color='text.secondary'>
          <span>{t('descriptions.RECOVERY_PAID_CANCELLED_ORDER')} </span>
          <strong>{notification.data?.orderCode ?? ''}</strong>
          <span> {t('recoveryPaidCancelledOrder.post')} </span>
          {notification.data?.incidence ? (
            <>
              <span>{t('recoveryPaidCancelledOrder.withIncidence')} </span>
              <strong>{notification.data.incidence.code}</strong>
            </>
          ) : (
            <span>{t('recoveryPaidCancelledOrder.noIncidence')}</span>
          )}
        </Typography>
      );
    }

    return (
      <Typography variant='body2' color='text.secondary'>
        <strong>{notification?.data?.email}</strong>
        <span> {t(`descriptions.${notification?.type}`)}</span>
      </Typography>
    );
  }, [
    notification.data?.email,
    notification.data?.name,
    notification.data?.file?.filename,
    notification.data?.incidence,
    notification.data?.orderCode,
    notification.message,
    notification.executedBy?.fullName,
    notification?.type,
    t,
  ]);

  return (
    <Box
      sx={{
        p: 2,
        minWidth: 300,
        maxWidth: 400,
        bgcolor: 'background.paper',
        borderRadius: 5,
        pointerEvents: 'auto',
        position: 'relative',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out',
      }}
      className='shadow-lg'
    >
      <Stack direction='row' spacing={2} alignItems='flex-start'>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          {getInitials(
            notification.data?.name,
            notification.data?.owner?.firstName,
            notification.data?.owner?.lastName,
          )}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack justifyContent={'space-between'} direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
            <FlexBox>
              <Typography variant='subtitle2' fontWeight={600} noWrap>
                {notificationTitle}
              </Typography>
              {!notification?.viewed && <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
            </FlexBox>
            <Tooltip title={t('common:close')}>
              <MuiIconButton
                size='small'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (toastId) {
                    toast.dismiss(toastId);
                  }
                  onDismiss?.();
                }}
                sx={{ p: 0.5 }}
              >
                <CloseOutlined fontSize='small' />
              </MuiIconButton>
            </Tooltip>
          </Stack>

          <div style={{ marginBottom: 8 }}>{notificationDescription}</div>

          {notification.type === NOTIFICATION_TYPE.EXPORT_DONE && (
            <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
              <Button
                size='small'
                variant='contained'
                onClick={(e) => {
                  e.stopPropagation();
                  const downloadId = notification.data?._id || notification.data?.path;
                  if (!downloadId) {
                    return;
                  }

                  // Prevent multiple downloads of the same file within 2 seconds
                  if (!checkDownloadLock(downloadId, 2000)) {
                    return;
                  }

                  const filename = notification.data?.file?.filename || notification.data?.name || 'export_file';
                  const lower = filename.toLowerCase();

                  if (lower.endsWith('.pdf')) {
                    InventoryDownloadService._downloadFileToPDF(downloadId, filename);
                    return;
                  }

                  InventoryDownloadService._downloadFileToExcel(downloadId, filename);
                }}
              >
                {t('actions.download', { defaultValue: 'Download' })}
              </Button>
            </Stack>
          )}

          <Typography variant='caption' color='text.secondary'>
            {formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
              locale: es,
            })}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default memo(NotificationToastItem);
